<script setup lang="ts">
/**
 * TourOverlay — spotlight-style guided tour ("Vodič kroz ovu stranicu"), ported from
 * customer-service/feature-crm. Steps target arbitrary elements via a CSS selector
 * (a `data-tour` anchor), so a step can point at an element in a different component
 * tree than the page that defines the tour. Mounted once, globally (see App.vue), and
 * driven by stores/tour.ts.
 *
 * A step whose selector matches nothing, or matches a hidden element (a tab that is
 * not open, a scope-gated button the user cannot see), is skipped rather than shown
 * empty. `activate` is a selector clicked first — a tab — so a step can walk into a
 * part of the page that is mounted but hidden.
 */
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import XIcon from '@/components/icons/XIcon.vue'

export interface TourStep {
    /** Optional route to navigate to before locating the selector below. */
    route?: string
    /**
     * Optional selector to click before locating `selector` — a tab, an accordion
     * header — so the anchor becomes visible. Skipped quietly when it matches nothing.
     */
    activate?: string
    selector: string
    tagKey: string
    titleKey: string
    bodyKey: string
}

const props = defineProps<{ steps: TourStep[] }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const { t } = useI18n()
const router = useRouter()

const stepIndex = ref(0)
const rect = ref<DOMRect | null>(null)
const placement = ref<'top' | 'bottom'>('bottom')

function sleep(ms: number) {
    return new Promise<void>((resolve) => window.setTimeout(resolve, ms))
}

const currentStep = computed<TourStep | undefined>(() => props.steps[stepIndex.value])
const isFirst = computed(() => stepIndex.value === 0)
const isLast = computed(() => stepIndex.value === props.steps.length - 1)

const TOOLTIP_WIDTH = 320
const GAP = 14
const PAD = 8

function isVisible(el: Element): boolean {
    const r = el.getBoundingClientRect()
    return r.width > 0 && r.height > 0
}

function locate() {
    const step = currentStep.value
    const el = step ? document.querySelector(step.selector) : null
    rect.value = el ? el.getBoundingClientRect() : null
    if (rect.value) {
        placement.value = rect.value.bottom + 220 > window.innerHeight ? 'top' : 'bottom'
    }
}

async function goTo(index: number, direction: 1 | -1 = 1) {
    if (index < 0 || index >= props.steps.length) {
        emit('close')
        return
    }
    const step = props.steps[index]

    if (step.route && router.currentRoute.value.path !== step.route) {
        // Hide the current spotlight/tooltip while the page changes underneath.
        rect.value = null
        try {
            await router.push(step.route)
        } catch {
            // Aborted/duplicate navigation — fall through and try to locate anyway.
        }
        await nextTick()
        // The destination page still needs to mount and fetch before the anchor exists.
        await sleep(450)
    } else {
        await nextTick()
    }

    if (step.activate) {
        const trigger = document.querySelector<HTMLElement>(step.activate)
        if (trigger) {
            trigger.click()
            await nextTick()
            await sleep(120)
        }
    }

    const el = document.querySelector(step.selector)
    // Target not present (scope-gated button) or present but hidden (a tab that is not
    // open) — skip past it rather than spotlight nothing.
    if (!el || !isVisible(el)) {
        goTo(index + direction, direction)
        return
    }
    stepIndex.value = index
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    window.setTimeout(locate, 260)
}

function next() {
    if (isLast.value) {
        emit('close')
    } else {
        goTo(stepIndex.value + 1, 1)
    }
}
function back() {
    goTo(stepIndex.value - 1, -1)
}

function onReflow() {
    locate()
}
function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') emit('close')
    else if (e.key === 'ArrowRight') next()
    else if (e.key === 'ArrowLeft') back()
}

onMounted(() => {
    goTo(0, 1)
    window.addEventListener('resize', onReflow)
    window.addEventListener('scroll', onReflow, true)
    window.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
    window.removeEventListener('resize', onReflow)
    window.removeEventListener('scroll', onReflow, true)
    window.removeEventListener('keydown', onKeydown)
})

const spotlightStyle = computed(() => {
    if (!rect.value) {
        return { top: 0, left: 0, width: 0, height: 0, boxShadow: 'none' }
    }
    return {
        top: `${rect.value.top - PAD}px`,
        left: `${rect.value.left - PAD}px`,
        width: `${rect.value.width + PAD * 2}px`,
        height: `${rect.value.height + PAD * 2}px`,
        boxShadow: '0 0 0 9999px rgba(15,23,42,0.62), 0 0 0 2px rgba(255,255,255,0.85)',
        transition:
            'top 200ms ease-out, left 200ms ease-out, width 200ms ease-out, height 200ms ease-out',
    }
})

const tooltipStyle = computed(() => {
    if (!rect.value) return { display: 'none' }
    const top = placement.value === 'bottom' ? rect.value.bottom + GAP : undefined
    const bottom =
        placement.value === 'top' ? window.innerHeight - rect.value.top + GAP : undefined
    const rawLeft = rect.value.left + rect.value.width / 2
    const left = Math.min(
        Math.max(rawLeft, TOOLTIP_WIDTH / 2 + 16),
        window.innerWidth - TOOLTIP_WIDTH / 2 - 16,
    )
    return {
        top: top !== undefined ? `${top}px` : 'auto',
        bottom: bottom !== undefined ? `${bottom}px` : 'auto',
        left: `${left}px`,
        transform: 'translateX(-50%)',
        transition: 'top 200ms ease-out, bottom 200ms ease-out, left 200ms ease-out',
    }
})
</script>

<template>
    <teleport to="body">
        <div class="fixed inset-0 z-[9999]" role="dialog" aria-modal="true">
            <!-- Spotlight: a box-shadow "hole" cut around the target. Pointer-events are
                 disabled so the underlying page stays interactive during the tour. -->
            <div class="pointer-events-none fixed rounded-lg" :style="spotlightStyle"></div>

            <div
                v-if="currentStep"
                data-testid="tour-tooltip"
                class="fixed z-[10000] w-80 rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-surface-card)] p-4 shadow-2xl"
                :style="tooltipStyle"
            >
                <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span
                        class="text-[10.5px] font-semibold uppercase tracking-wide text-[var(--color-brand-600)]"
                    >
                        {{ t(currentStep.tagKey) }}
                    </span>
                    <button
                        type="button"
                        class="leading-none text-[var(--color-ink-muted)] transition-colors hover:text-[var(--color-ink-strong)]"
                        :aria-label="t('common.actions.close')"
                        @click="emit('close')"
                    >
                        <XIcon class="h-4 w-4" />
                    </button>
                </div>
                <h3 class="mb-1.5 text-sm font-semibold text-[var(--color-ink-strong)]">
                    {{ t(currentStep.titleKey) }}
                </h3>
                <p class="mb-4 text-[13px] leading-relaxed text-[var(--color-ink-base)]">
                    {{ t(currentStep.bodyKey) }}
                </p>
                <div class="flex flex-wrap items-center justify-between gap-2">
                    <span class="font-mono text-[11px] text-[var(--color-ink-muted)]">
                        {{ t('pageTour.controls.stepOf', { current: stepIndex + 1, total: steps.length }) }}
                    </span>
                    <div class="flex gap-2">
                        <button
                            v-if="!isFirst"
                            type="button"
                            class="rounded-md border border-[var(--color-border-strong)] px-3 py-1.5 text-xs font-medium text-[var(--color-ink-base)] transition-colors hover:bg-[var(--color-surface-sunken)]"
                            @click="back"
                        >
                            {{ t('pageTour.controls.back') }}
                        </button>
                        <button
                            type="button"
                            class="rounded-md bg-[var(--color-brand-600)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[var(--color-brand-500)]"
                            @click="next"
                        >
                            {{ isLast ? t('pageTour.controls.done') : t('pageTour.controls.next') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>
